import { getHandleResponse } from "helpers/getHandleResponse";
import { getAuthHeader } from "helpers/getAuthHeader";

const API_BASE_URL = process.env.REACT_APP_URL;

const makeRequest = async (endpoint, method, data = null) => {
    const requestOptions = { method, headers: getAuthHeader() };
    if (data) requestOptions.body = JSON.stringify(data);
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
        const result = await getHandleResponse(response);
        return { error: false, data: result };
    } catch (error) {
        return { error: true, data: error.message };
    }
};

const makeFileRequest = async (endpoint, method, formData) => {
    const requestOptions = {
        method,
        headers: { 'Authorization': getAuthHeader().Authorization },
        body: formData,
    };
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
        const result = await getHandleResponse(response);
        return { error: false, data: result };
    } catch (error) {
        return { error: true, data: error.message };
    }
};

const makeFileRequestWithErrors = async (endpoint, method, formData) => {
    const requestOptions = {
        method,
        headers: { Authorization: getAuthHeader().Authorization },
        body: formData,
    };
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
        const result = await response.json();
        if (!response.ok) {
            return { error: true, message: result.message, errors: result.errors || [], data: result };
        }
        return { error: false, data: result };
    } catch (error) {
        return { error: true, message: error.message, errors: [], data: null };
    }
};

const makeBlobDownload = async (endpoint, method = 'GET', body = null, filename = 'descarga') => {
    const requestOptions = {
        method,
        headers: {
            Authorization: getAuthHeader().Authorization,
            ...(body ? { 'Content-Type': 'application/json' } : {})
        },
        ...(body ? { body: JSON.stringify(body) } : {})
    };
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
        if (!response.ok) throw new Error('Error al descargar archivo');
        const disposition = response.headers.get('Content-Disposition');
        let downloadName = filename;
        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match) downloadName = match[1];
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return { error: false };
    } catch (error) {
        return { error: true, data: error.message };
    }
};

const buildQueryString = (params) => {
    const filteredParams = Object.entries(params)
        .filter(([_, value]) => value !== null && value !== undefined && value !== '')
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    return new URLSearchParams(filteredParams).toString();
};

const invoiceService = {
    createInvoice: (formData) => makeFileRequestWithErrors("/api/invoices", "POST", formData),

    getInvoices: (params = {}) => {
        const queryString = buildQueryString(params);
        return makeRequest(`/api/invoices${queryString ? `?${queryString}` : ''}`, "GET");
    },

    getInvoiceStats: (proveedor_id = null) => {
        const queryString = proveedor_id ? `?proveedor_id=${proveedor_id}` : '';
        return makeRequest(`/api/invoices/stats${queryString}`, "GET");
    },

    getInvoicesByMonth: (year, month, proveedor_id = null) => {
        const queryString = proveedor_id ? `?proveedor_id=${proveedor_id}` : '';
        return makeRequest(`/api/invoices/month/${year}/${month}${queryString}`, "GET");
    },

    getInvoiceById: (id) => makeRequest(`/api/invoices/${id}`, "GET"),

    updateInvoiceStatus: (id, data) => makeRequest(`/api/invoices/${id}/status`, "PATCH", data),

    updateInvoice: (id, formData) => makeFileRequest(`/api/invoices/${id}`, "PUT", formData),

    updateInvoiceFiles: (id, formData) => makeFileRequest(`/api/invoices/${id}/files`, "PUT", formData),

    updateInvoiceData: (id, data) => makeRequest(`/api/invoices/${id}/data`, "PATCH", data),

    downloadInvoiceFile: async (id, tipo) => {
        const requestOptions = { method: 'GET', headers: getAuthHeader() };
        try {
            const response = await fetch(`${API_BASE_URL}/api/invoices/${id}/download/${tipo}`, requestOptions);
            if (!response.ok) throw new Error('Error al descargar archivo');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `factura_${tipo}_${id}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            return { error: false, data: 'Archivo descargado' };
        } catch (error) {
            return { error: true, data: error.message };
        }
    },
    viewInvoiceFileUrl: (id, tipo) =>
        `${API_BASE_URL}/api/invoices/${id}/view/${tipo}`,

    // ── NUEVO: Descarga masiva (admin) ─────────────────────────────────────────
    bulkDownloadPreview: (params = {}) => {
        const queryString = buildQueryString(params);
        return makeRequest(`/api/invoices/bulk-download/preview${queryString ? `?${queryString}` : ''}`, "GET");
    },

    bulkDownload: (body) =>
        makeBlobDownload(
            '/api/invoices/bulk-download',
            'POST',
            body,
            `facturas_${new Date().toISOString().split('T')[0]}.zip`
        ),

    // ── NUEVO: Comprobante de pago — admin sube y marca pagada ─────────────────
    uploadPaymentProof: (invoiceId, formData) =>
        makeFileRequest(`/api/invoices/${invoiceId}/payment-proof`, "POST", formData),

    // ── NUEVO: Complemento de pago — proveedor sube (solo PPD) ────────────────
    uploadComplementoPago: (invoiceId, formData) =>
        makeFileRequest(`/api/invoices/${invoiceId}/complemento-pago`, "POST", formData),
};

export default invoiceService;